import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { Receipt, ReceiptStatus } from "@/types/receipt";
import { TransactionSuggestion } from "@/types/payments";
import { settledRefusalOrNull } from "@/utils/errorUtils";
import { handleError } from "@/utils/handleError";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

/**
 * Client for the Receipt API edge function
 */
export class ReceiptApiClient {
  async getReceipts(statusFilter?: ReceiptStatus): Promise<Receipt[]> {
    try {
      const body: Record<string, unknown> = { action: "getReceipts" };
      if (statusFilter !== undefined) {
        body.statusFilter = statusFilter;
      }
      const { data, error } = await supabase.functions.invoke("receipt-api", { body });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching receipts');
      return data as Receipt[];
    } catch (error) {
      console.error("Failed to fetch receipts:", error);
      throw error;
    }
  }

  async getReceiptById(receiptId: string): Promise<Receipt> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "getReceiptById", receiptId }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching receipt');
      return data as Receipt;
    } catch (error) {
      console.error("Failed to fetch receipt:", error);
      throw error;
    }
  }

  async createReceipt(financialDocumentId: string, amount: number, currency: string): Promise<Receipt> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "createReceipt", financialDocumentId, amount, currency }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error creating receipt');
      return data as Receipt;
    } catch (error) {
      console.error("Failed to create receipt:", error);
      throw error;
    }
  }

  async matchReceipt(receiptId: string, transaction: { id: string; amount: number; currency: string; date: string; bankAccountId: string }): Promise<Receipt> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "matchReceipt", receiptId, transaction }
      });
      if (error) {
        // FIN-968: the transaction already settles another document -- a named refusal the
        // page shows as-is. Anything else (e.g. FIN-1052's amount refusal) is toasted here with
        // the server's own sentence, as ManualMatchApiClient does.
        const { settled, body } = await settledRefusalOrNull(error);
        if (settled) throw settled;
        await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToMatchReceipt'), 'matchReceipt', body);
        throw error;
      }
      return data as Receipt;
    } catch (error) {
      console.error("Failed to match receipt:", error);
      throw error;
    }
  }

  async markAsMatchedExternally(receiptId: string, matchedAt: string, actualSekAmount?: number): Promise<Receipt> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "markAsMatchedExternally", receiptId, matchedAt, actualSekAmount }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error marking receipt as matched externally');
      return data as Receipt;
    } catch (error) {
      console.error("Failed to mark receipt as matched externally:", error);
      throw error;
    }
  }

  /**
   * Acknowledge a receipt as matched externally when a bank account is connected but no matching
   * transaction was found (issue #498, design plan's case c — "unmatched acknowledged"). The
   * backend derives the confirming user from the authenticated session — never pass a userId
   * here. Distinct from markAsMatchedExternally above: this routes to a service_role-only atomic
   * RPC that writes an audit row — see ReceiptService.acknowledgeExternalMatch's docstring
   * (supabase/functions/_shared/services/receiptService.ts). Mirrors
   * PaymentApiClient.acknowledgeExternalPayment's shape, including the actualSekAmount parameter
   * (unlike ReceivableApiClient.acknowledgeExternalPayment, whose backend action does not accept
   * one).
   */
  async acknowledgeExternalMatch(receiptId: string, matchedAt: string, actualSekAmount?: number): Promise<Receipt> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "acknowledgeExternalMatch", receiptId, matchedAt, actualSekAmount }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error acknowledging external match');
      return data as Receipt;
    } catch (error) {
      console.error("Failed to acknowledge external match:", error);
      throw error;
    }
  }

  async getTransactionSuggestions(financialDocumentId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "getTransactionSuggestions", financialDocumentId }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching transaction suggestions');
      return data as TransactionSuggestion[];
    } catch (error) {
      console.error("Failed to fetch transaction suggestions:", error);
      throw error;
    }
  }

  /**
   * Precomputed candidate transactions (issue #457) — reads the async match_jobs pipeline's
   * already-computed auto_match_decisions suggestion instead of a live JIT scoring query.
   */
  async getPrecomputedTransactionSuggestions(financialDocumentId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke("receipt-api", {
        body: { action: "getPrecomputedTransactionSuggestions", financialDocumentId }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching precomputed transaction suggestions');
      return data as TransactionSuggestion[];
    } catch (error) {
      console.error("Failed to fetch precomputed transaction suggestions:", error);
      throw error;
    }
  }
}
